import { useState } from 'react';
import { twoFactorApiClient, type TwoFactorEnrollment } from '../../auth/api/twoFactorApiClient';
import { useAuthStore } from '../../auth/store';
import { handleApiError } from '../../../shared/utils/handleApiError';
import { useToast } from '../../../shared/components/toast/useToast';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { TwoFactorQrPanel, BackupCodesPanel } from '../../auth/components/TwoFactorPanels';

type Step = 'idle' | 'scan' | 'codes' | 'disable';

/**
 * Account security — TOTP two-factor setup (PE-2157, backend PE-2156).
 *
 * Self-service only: enrol, confirm, disable. Every route here needs a Bearer
 * token, so this page is only reachable by someone already signed in. The
 * mandated-but-unenrolled lockout is handled elsewhere by design — it has no
 * session to reach this page with, and enrols through the @Public pendingToken
 * routes on TwoFactorEnrollmentForm instead. The QR and backup-code blocks are
 * shared with that screen via TwoFactorPanels so the two cannot drift.
 *
 * No permission gate: 2FA is a property of your own account, and watchman guards
 * these routes with the session alone (@Controller() + the global AuthGuard, no
 * @RequiresPermission). Gating the page on a permission would lock people out of
 * their own security settings.
 */
export const SecurityPage = () => {
  const { toast } = useToast();
  const { twoFactorEnabled, setTwoFactorEnabled } = useAuthStore();

  const [step, setStep] = useState<Step>('idle');
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setStep('idle');
    setEnrollment(null);
    setCode('');
    setBackupCodes([]);
    setCodesAcknowledged(false);
    setError('');
  };

  const startEnrollment = async () => {
    setIsBusy(true);
    setError('');
    try {
      setEnrollment(await twoFactorApiClient.enroll());
      setCode('');
      setStep('scan');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsBusy(false);
    }
  };

  const confirmEnrollment = async () => {
    setIsBusy(true);
    setError('');
    try {
      const { backupCodes: codes } = await twoFactorApiClient.confirm(code.trim());
      setBackupCodes(codes);
      setCodesAcknowledged(false);
      setStep('codes');
      setTwoFactorEnabled(true);
      toast.success('Two-step verification is on');
    } catch (err) {
      // A 401 here is either a wrong code or a lapsed 10-minute enrolment window.
      // Both are recoverable in place — the user retries, or restarts to get a
      // fresh QR — so neither should tear the page down.
      setError(handleApiError(err));
    } finally {
      setIsBusy(false);
    }
  };

  const disable = async () => {
    setIsBusy(true);
    setError('');
    try {
      await twoFactorApiClient.disable(code.trim());
      setTwoFactorEnabled(false);
      toast.success('Two-step verification is off');
      reset();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Security</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Two-step verification adds a code from your phone to your password.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-foreground">Two-step verification</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {twoFactorEnabled === true && 'On for this account.'}
              {twoFactorEnabled === false && 'Off for this account.'}
              {/* Status comes from /auth/me, which hydrateSession fetches best-effort.
                  Null means that call failed, not that the server has no answer —
                  anything other than "we do not know" would be invented, so both
                  actions stay available in that state. */}
              {twoFactorEnabled === null &&
                'Current status could not be loaded. Sign out and back in to refresh it, or use the action that matches what you want to do.'}
            </p>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {step === 'idle' && (
          <div className="flex flex-wrap gap-3">
            {twoFactorEnabled !== true && (
              <Button onClick={startEnrollment} loading={isBusy}>
                Set up two-step verification
              </Button>
            )}
            {twoFactorEnabled !== false && (
              <Button
                variant="outline"
                onClick={() => {
                  setCode('');
                  setError('');
                  setStep('disable');
                }}>
                Turn it off
              </Button>
            )}
          </div>
        )}

        {step === 'scan' && enrollment && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Scan this with your authenticator app, then enter the 6-digit code it shows.
            </p>
            <TwoFactorQrPanel enrollment={enrollment} />
            <Input
              name="totp"
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              autoComplete="one-time-code"
            />
            <div className="flex gap-3">
              <Button onClick={confirmEnrollment} loading={isBusy} disabled={!code.trim()}>
                Confirm
              </Button>
              <Button variant="outline" onClick={reset}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {step === 'codes' && (
          <div className="space-y-4">
            <BackupCodesPanel
              codes={backupCodes}
              acknowledged={codesAcknowledged}
              onAcknowledgedChange={setCodesAcknowledged}
            />
            {/* Gated on the checkbox because dismissing here destroys the only
                copy — the server never shows them again. */}
            <Button onClick={reset} disabled={!codesAcknowledged}>
              Done
            </Button>
          </div>
        )}

        {step === 'disable' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Enter a current code from your authenticator app, or an unused backup code, to turn
              two-step verification off.
            </p>
            <Input
              name="disable-code"
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              autoComplete="one-time-code"
            />
            <div className="flex gap-3">
              <Button variant="secondary" onClick={disable} loading={isBusy} disabled={!code.trim()}>
                Turn off
              </Button>
              <Button variant="outline" onClick={reset}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
